/**
 * FullStacked Cloud - Centralized SSO redirect, exchange-token handoff and console session storage.
 *
 * Console side: no session -> generate a PKCE pair (the code_verifier is kept in sessionStorage)
 * and redirect to `${auth}/?callbackUrl=<location>&code_challenge=<S256>&code_challenge_method=S256`
 * (an invitation's `email` and `invite` move from the Console URL to the auth URL). On return with
 * `?exchange_token=fsc_exc_...` the token is redeemed with the verifier
 * (POST /v1/auth/exchange/token { exchangeToken, codeVerifier }), the session is stored in
 * sessionStorage and the token is removed from the URL with history.replaceState.
 * (Minting the exchange token on the auth side is owned by the auth UI.)
 */

import type { SessionPayload } from "./api/types.ts";
import { createPkcePair } from "./lib/pkce.ts";

export const SESSION_KEY = "fsc_console_session";
/** PKCE code_verifier of the pending SSO redirect (sessionStorage, removed once redeemed). */
export const PKCE_VERIFIER_KEY = "fsc_console_pkce_verifier";
/** Invitation landing parameters (notification.md §3.3) forwarded to the auth UI. */
const FORWARDED_PARAMS = ["email", "invite"] as const;

type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Reads a stored session; returns null when missing, malformed or expired. */
export function loadSession(storage: KeyValueStorage, nowMs = Date.now()): SessionPayload | null {
    let parsed: SessionPayload;
    try {
        const raw = storage.getItem(SESSION_KEY);
        if (!raw) return null;
        parsed = JSON.parse(raw) as SessionPayload;
    } catch {
        return null;
    }
    if (!parsed || typeof parsed.token !== "string" || !parsed.token.startsWith("fsc_"))
        return null;
    const exp = parsed.expiresAt ?? parsed.session?.expiresAt;
    if (typeof exp === "number" && (exp < 1e12 ? exp * 1000 : exp) <= nowMs) return null;
    return parsed;
}

export function saveSession(storage: KeyValueStorage, payload: SessionPayload): void {
    storage.setItem(SESSION_KEY, JSON.stringify(payload));
}

export function clearSession(storage: KeyValueStorage): void {
    storage.removeItem(SESSION_KEY);
}

export function extractExchangeToken(href: string): string | null {
    const url = new URL(href);
    const token = url.searchParams.get("exchange_token");
    return token && token.startsWith("fsc_exc_") ? token : null;
}

/** Returns the path+query+hash of `href` without the exchange token parameter. */
export function stripExchangeToken(href: string): string {
    const url = new URL(href);
    url.searchParams.delete("exchange_token");
    const qs = url.searchParams.toString();
    return `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`;
}

/**
 * `<auth>/?[email=&invite=&]callbackUrl=<location>&code_challenge=<S256>&code_challenge_method=S256`.
 * `email` and `invite` (invitation links) are moved from the callback URL to the auth URL so the
 * sign-in view opens pre-filled and the Console returns to a clean resource URL.
 */
export function buildAuthRedirectUrl(
    authBase: string,
    callbackUrl: string,
    codeChallenge: string
): string {
    const base = authBase.startsWith("http") ? authBase : `https://${authBase}`;
    const target = new URL("/", base);
    const callback = new URL(callbackUrl);
    for (const key of FORWARDED_PARAMS) {
        const value = callback.searchParams.get(key);
        if (value) target.searchParams.set(key, value);
        callback.searchParams.delete(key);
    }
    callback.searchParams.delete("exchange_token");
    target.searchParams.set("callbackUrl", callback.toString());
    target.searchParams.set("code_challenge", codeChallenge);
    target.searchParams.set("code_challenge_method", "S256");
    return target.toString();
}

/** Reads the pending PKCE verifier once: it is removed so an exchange token is redeemed once. */
export function takeCodeVerifier(storage: KeyValueStorage): string | null {
    const verifier = storage.getItem(PKCE_VERIFIER_KEY);
    storage.removeItem(PKCE_VERIFIER_KEY);
    return verifier || null;
}

/** Creates the PKCE pair, keeps the verifier and returns the auth URL to redirect to. */
export async function prepareAuthRedirect(
    storage: KeyValueStorage,
    authBase: string,
    callbackUrl: string
): Promise<string> {
    const pkce = await createPkcePair();
    storage.setItem(PKCE_VERIFIER_KEY, pkce.codeVerifier);
    return buildAuthRedirectUrl(authBase, callbackUrl, pkce.codeChallenge);
}

/* ---------- Browser wrappers ---------- */

export async function redirectToAuth(authBase: string): Promise<void> {
    const target = await prepareAuthRedirect(window.sessionStorage, authBase, window.location.href);
    window.location.assign(target);
}

export function cleanExchangeTokenFromUrl(): void {
    window.history.replaceState(
        window.history.state,
        document.title,
        stripExchangeToken(window.location.href)
    );
}
